import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// order-entry
import { OEModelMasterService } from "./oe-model-master.service";
import { OENDisplayState } from "../../data/enums/oe-display-state.enum";
import { OENOperationMode } from "../../data/enums/oe-operation-mode.enum";
import {
  OEPrivatizationLockingProfileModel,
  OELockingProfileModel,
} from "../../data/models/oe-locking-profile.model";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OEDataModel } from "../../data/models/oe-data.model";
import { OEStrategyModel } from "../../data/models/oe-strategy.model";
import { OENEntryType } from "../../data/enums/oe-entry-type.enum";
import { ICAutocompleteGenericModel } from "../../interface/controls/models/bfx-interface-controls.models";
import { v4 as uuid } from "uuid";
import { OESettingsService } from "app/order-entry/services/basic/oe-settings.service";
import { ORDER_STATE_REJECTED } from "shared/cubiq-services/model/HedgeOrderMonitorStagingTableDefinitionFactory.constants";

const TERMINAL_DISPLAY_STATES: OENDisplayState[] = [
  OENDisplayState.SHOW_SUCCESS,
  OENDisplayState.SHOW_MONITOR_EXECUTION,
  OENDisplayState.SHOW_REJECTED,
  OENDisplayState.SHOW_EXPIRY_PENDING,
  OENDisplayState.SHOW_UNLOCKED,
  OENDisplayState.SHOW_LOST_HIDDEN,
];

@Injectable()
export class OEModelGetterService {
  /**********************************************************************/
  /* CONSTRUCTOR*/
  constructor(private master: OEModelMasterService, private settings: OESettingsService) {}

  /**********************************************************************/
  /*  MASTER API  */
  public has(refId: string): boolean {
    return this.master.has(refId);
  }

  public hasStrategy(refId: string): boolean {
    return this.master.hasStrategy(refId);
  }

  public get(refId: string): OEDataModel {
    return this.master.get(refId);
  }

  public getAllRootRefs(): Array<string> {
    return this.master.getAllRootRefs();
  }

  public instance(refId: string): OEDataModel {
    return this.master.instance(refId);
  }

  public instanceNull(refId: string): OEDataModel | null {
    return this.master.get(refId);
  }

  public validateRefFormat(refId: string): boolean {
    return this.master.validateRefFormat(refId);
  }

  public isBenchmarkRequest(refId: string) {
    return this.master.isBenchmarkRequest(refId);
  }

  /**********************************************************************/
  /*  orderId */
  public getOrderId(refId: string): string {
    if (this.master.has(refId)) {
      return this.master.get(refId).orderId;
    }
    return null;
  }

  public getOrderRef(refId: string): string {
    if (this.master.has(refId)) {
      return this.master.get(refId).orderRef;
    }
    return null;
  }

  public getStrategyRef(refId: string): string {
    if (this.master.has(refId)) {
      return this.master.get(refId).strategyRef;
    }
    return null;
  }

  /**
   * NOTE: used for modify
   * This method returns the orderId of the model with refId passed argument.
   * If the orderId is null (new leg with refId was just created), it returns the orderId for root.
   * A valid order id is needed for lock on modify.
   */
  public getOrderIdForRefIdOrRoot(refId: string): string {
    let orderId = this.getOrderId(refId);
    if (orderId == null) {
      try {
        let rootRefId = this.getRootIdForNodeWithRefId(refId);
        orderId = this.getOrderId(rootRefId);
      } catch (ex) {
        return null;
      }
    }
    return orderId;
  }

  /**
   * Find closest nearest ancestor that is active. If none are active, then get all nodes and try to find any active.
   * We need an active leg for monitoring initial acceptance events on modification, root or selected tree might be filled
   *
   * @param refId
   * @returns
   */
  public getOrderIdOfActiveStrategyLeg(refId): string {
    const origRefId = refId;
    // CHeck for legs that are terminal or never got an orderId
    while ((this.getOrderId(refId) == null || !this.isActiveLeg(refId)) && this.hasParent(refId)) {
      refId = this.getParentRef(refId);
    }
    let found = false;
    if (this.isInTerminalState(refId)) {
      // We've gone from refId to root and none are active so need to check all legs
      const allIds = this.getStrategyNodesListRoot(refId);
      for (refId of allIds) {
        if (this.isActiveLeg(refId) && this.getOrderId(refId) !== null) {
          found = true;
          break;
        }
      }
    } else {
      found = true;
    }
    return this.getOrderId(found ? refId : origRefId);
  }

  /**
   * Is the specified order is in an active state considering the operation mode?
   * For MODIFY, REJECTED is a valid active state.
   * @param refId
   * @returns
   */
  private isActiveLeg(refId): boolean {
    // Check that order is active - REJECTED must be considered active here if mode is modify as
    // once an order is REJECTED it's srvState might not be rolled back to its prior state
    const order = this.get(refId);
    const state = order.srvOrderState;
    const mode = order.operationMode;

    switch (state) {
      case CubiqTS.OrderState.EXPIRED:
      case CubiqTS.OrderState.CANCELLED:
      case CubiqTS.OrderState.FILLED:
      case null:
        return false;
      case CubiqTS.OrderState.REJECTED:
        return mode === OENOperationMode.MODIFY;
    }
  }

  public getOrderRefForRefIdOrRoot(refId: string): string {
    let orderId = this.getOrderIdForRefIdOrRoot(refId);
    return this.getOrderRef(orderId);
  }

  public getRefIdForOrderId(contextRefId: string, orderId: string): string {
    if (this.master.has(contextRefId)) {
      if (this.getOrderId(contextRefId) === orderId) {
        return contextRefId;
      } else {
        let result: string = null;
        this.getStrategyNodesListRoot(contextRefId).forEach((itemRefId: string) => {
          if (this.getOrderId(itemRefId) === orderId) {
            result = itemRefId;
          }
        });
        return result;
      }
    }
    return null;
  }

  /* ******************************************** */
  /* RefID <=> Order id*/
  /**
   * Returns refId given an orderId.
   * @param orderId <string> - to be matched by an instance in the model
   * @param rootRefId <string> - limits the search by defining the scope to strategy connected orders/legs related to this refId
   */
  public findRefIdForOrderId(orderId: string, rootRefId: string = null): string {
    let match: string = null;
    if (orderId != null && typeof orderId === "string" && orderId !== "") {
      let nodes: Array<string> =
        rootRefId != null && this.master.has(rootRefId)
          ? this.getStrategyNodesListRoot(rootRefId)
          : this.master.getAllRefs();
      if (nodes != null && nodes.length > 0) {
        nodes.forEach((itemRefId: string) => {
          if (this.master.has(itemRefId) && this.master.get(itemRefId).orderId === orderId) {
            match = itemRefId;
          }
        });
      }
    }
    return match;
  }

  /**********************************************************************/
  /* OrderID of the MODIFY issuer  */
  public getModifyIssuerOrderId(refId: string): string {
    if (this.master.has(refId)) {
      return this.master.instance(refId).modifyIssuedOrderId;
    }
    return null;
  }

  public getModifyIssuerRefId(contextRefId: string): string {
    if (this.master.has(contextRefId)) {
      if (this.isThisTheModifyIssuer(contextRefId)) {
        return contextRefId;
      } else {
        let orderId = this.getModifyIssuerOrderId(contextRefId);
        let issuerRefId = this.getRefIdForOrderId(contextRefId, orderId);
        return issuerRefId;
      }
    }
    return null;
  }

  public isThisTheModifyIssuer(refId: string): boolean {
    if (this.master.has(refId) && this.getOrderId(refId) != null) {
      // issuer must have orderId
      return this.master.instance(refId).modifyIssuedOrderId === this.getOrderId(refId);
    }
    return false;
  }

  public getOrderStateForModifyIssuer(contextRefId: string): CubiqTS.OrderState {
    if (this.master.has(contextRefId)) {
      let issuerOrderId = this.getModifyIssuerOrderId(contextRefId);
      let issuerRefId = this.getRefIdForOrderId(contextRefId, issuerOrderId);
      if (this.master.has(issuerRefId)) {
        return this.getSrvOrderState(issuerRefId);
      }
    }
    return null;
  }

  public getActionableOrderId(refId: string): string {
    try {
      let orderId: string = null;
      if (this.isOperationModeModify(refId)) {
        // MODIFY
        orderId = this.getModifyIssuerOrderId(refId);
        orderId = orderId != null ? orderId : this.master.get(refId).orderId;
      } else {
        // NEW & DUPLICATE & MODIFY INFO & AMEND MARKUP
        orderId = this.getOrderIdForRefIdOrRoot(refId);
      }
      return orderId;
    } catch (ex) {
      logger.out.error(`EXCEPTION: _oeWorker.getIssuerOrderId(): %O`, ex);
      return null;
    }
  }

  /**********************************************************************/
  /* LIFESPAN - Terminal */

  public isInTerminalState(refId: string): boolean {
    if (this.master.has(refId)) {
      return this.master.instance(refId).isInTerminalState;
    }
    return null;
  }

  public isNotInTerminalState(refId: string): boolean {
    if (this.master.has(refId)) {
      return !this.master.instance(refId).isInTerminalState;
    }
    return null;
  }

  public parentIsInTerminalState(refId: string): boolean {
    if (this.master.hasStrategy(refId)) {
      return this.isInTerminalState(this.master.instance(refId).strategy.parentRefId);
    }
    return null;
  }

  /**********************************************************************/
  /* LIFESPAN - Initialization */

  public isInInitialization(refId: string): boolean {
    return this.master.has(refId) ? this.master.get(refId).isInInitialization : false;
  }

  /**********************************************************************/
  /* Request Model  */

  public getRequestModel(refId: string): any {
    let ret: any = null;
    if (this.master.has(refId)) {
      let rootRefId = this.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        ret = this.master.get(rootRefId).requestModel;
        if (!ret) {
          ret = this.master.get(rootRefId).bmarkRequestModel;
        }
      }
    }
    return ret;
  }

  public getModifyInfoRequestModel(refId: string): any {
    if (this.master.has(refId)) {
      let rootRefId = this.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        return this.master.get(rootRefId).requestModifyInfoModel;
      }
    }
    return null;
  }

  /**********************************************************************/
  /* Subscription Life-Cycle, Intervention & Scratchpad */

  public isSubscriptionToLifeCycleEventsActive(refId: string): boolean {
    if (this.master.has(refId)) {
      return this.master.get(refId).subscriptionLifeCycleActive;
    }
    return false;
  }

  public determineLifeCycleActiveSubscriptions(): number {
    let result: number = 0;
    for (let item of this.master.getAll().values()) {
      if (item.subscriptionLifeCycleActive) {
        result++;
      }
    }
    return result;
  }

  public isSubscriptionToInterventionEventsActive(refId: string): boolean {
    if (this.master.has(refId)) {
      return this.master.get(refId).subscriptionInterventionActive;
    }
    return false;
  }

  public determineInterventionActiveSubscriptions(): number {
    let result: number = 0;
    for (let item of this.master.getAll().values()) {
      if (item.subscriptionInterventionActive) {
        result++;
      }
    }
    return result;
  }

  public isSubscriptionToScratchpadEventsActive(refId: string): boolean {
    if (this.master.has(refId)) {
      return this.master.get(refId).subscriptionScratchpadActive;
    }
    return false;
  }

  public determineScratchpadActiveSubscriptions(): number {
    let result: number = 0;
    for (let item of this.master.getAll().values()) {
      if (item.subscriptionScratchpadActive) {
        result++;
      }
    }
    return result;
  }

  /**********************************************************************/
  /* Display state */

  public getDisplayState(refId: string): OENDisplayState {
    try {
      return this.master.has(refId) ? this.master.get(refId).displayState : null;
    } catch (ex) {}
  }

  public getDisplayStateForRoot(refId: string): OENDisplayState {
    if (this.master.has(refId)) {
      let rootRefId = this.getRootIdForNodeWithRefId(refId);
      try {
        return this.master.get(rootRefId).displayState;
      } catch (ex) {
        return null;
      }
    }
    return null;
  }

  public isOrderDisplayStateTerminal(refId: string): boolean {
    const displayState = this.getDisplayState(refId);
    if (displayState != null) {
      return TERMINAL_DISPLAY_STATES.indexOf(displayState) !== -1;
    }
    return false;
  }

  public isOrderDisplayState(refId: string, state: OENDisplayState): boolean {
    return state != null ? this.getDisplayState(refId) === state : false;
  }

  /**********************************************************************/
  /* Actively Displayed */
  public isActivelyDisplayed(refId: string): boolean {
    if (this.master.has(refId)) {
      return this.master.get(refId).isActivelyDisplayed;
    }
    return false;
  }

  public isSavedOrder(refId: string): boolean {
    return this.master.isSavedOrder(refId);
  }

  public getActivelyDisplayedRefId(contextRefId: string): string {
    if (this.master.has(contextRefId)) {
      if (this.master.get(contextRefId).isActivelyDisplayed) {
        return contextRefId;
      } else {
        let result: string = null;
        this.getStrategyNodesListRoot(contextRefId).forEach((itemRefId: string) => {
          if (this.master.get(itemRefId).isActivelyDisplayed) {
            result = itemRefId;
          }
        });
        return result;
      }
    }
    return null;
  }

  /**********************************************************************/
  /* Order state */
  public getSrvOrderState(refId: string): CubiqTS.OrderState {
    try {
      return this.master.has(refId) ? this.master.get(refId).srvOrderState : null;
    } catch (ex) {
      return null;
    }
  }

  public isSrvOrderState(refId: string, _state: CubiqTS.OrderState): boolean {
    let state = this.getSrvOrderState(refId);
    return state === _state;
  }

  // CHANGE: determineIfInTerminalState
  public isSrvOrderStateTerminal(refId: string): boolean {
    if (this.master.has(refId)) {
      return this.master.get(refId).isInTerminalState;
    }
    return false;
  }

  /**********************************************************************/
  /* Filled State */
  public hasBeenPartiallyFilled(refId: string): boolean {
    if (this.master.has(refId)) {
      return this.master.get(refId).fillState === CubiqTS.FillState.PARTFILLED;
    }
    return false;
  }

  public hasBeenFullyFilled(refId: string): boolean {
    if (this.master.has(refId)) {
      return this.master.get(refId).fillState === CubiqTS.FillState.FILLED;
    }
    return false;
  }

  public hasNotBeenFilled(refId: string): boolean {
    if (this.master.has(refId)) {
      return (
        this.master.get(refId).fillState === CubiqTS.FillState.NONE ||
        this.master.get(refId).fillState == null
      );
    }
    return false;
  }

  public hasNotBeenFilledParent(refId: string): boolean {
    let parentModel = this.getParentModel(refId);
    if (parentModel != null) {
      let parentFillState: CubiqTS.FillState = this.getParentFillState(refId);
      return parentFillState === CubiqTS.FillState.NONE || parentFillState == null;
    }
    return false;
  }

  public getParentFillState(refId): CubiqTS.FillState {
    if (this.master.hasStrategy(refId) && this.getParentModel(refId) != null) {
      return this.getParentModel(refId).fillState;
    }
    return null;
  }

  public getFillState(refId: string): CubiqTS.FillState {
    return this.master.has(refId) ? this.master.get(refId).fillState : null;
  }

  /**********************************************************************/
  /* Operation mode */

  public isOperationModeModify(refId: string): boolean {
    return (
      this.getOperationMode(refId) === OENOperationMode.MODIFY ||
      this.getOperationMode(refId) === OENOperationMode.MODIFY_SCRATCHPAD
    );
  }

  public isOperationModeModifyInfo(refId: string): boolean {
    return this.getOperationMode(refId) === OENOperationMode.MODIFY_INFO;
  }

  public isOperationModeNew(refId: string): boolean {
    return this.getOperationMode(refId) === OENOperationMode.NEW;
  }

  public isOperationModeDuplicate(refId: string): boolean {
    return this.getOperationMode(refId) === OENOperationMode.DUPLICATE;
  }

  public isOperationModeAmendMarkup(refId: string): boolean {
    return this.getOperationMode(refId) === OENOperationMode.AMEND_MARKUP;
  }

  public isOperationModeMonitorExecution(refId: string): boolean {
    return this.getOperationMode(refId) === OENOperationMode.MONITOR_EXECUTION;
  }

  public isOperationModePrivatizable(refId: string): boolean {
    return (
      this.isOperationModeModify(refId) ||
      this.isOperationModeModifyInfo(refId) ||
      this.isOperationModeAmendMarkup(refId)
    );
  }

  public isOperationModeEdit(refId: string): boolean {
    return this.getOperationMode(refId) === OENOperationMode.EDIT;
  }

  public isOperationModeModifyScratchpad(refId: string): boolean {
    return this.getOperationMode(refId) === OENOperationMode.MODIFY_SCRATCHPAD;
  }

  public isGammaCustomizeMode(refId: string): boolean {
    let customizeMode: boolean = false;

    if (this.master.has(refId)) {
      let data: OEDataModel = this.master.get(refId);
      if (data != null && data.gammaModel != null) {
        customizeMode = data.gammaModel.customizeMode === true;
      }
    }

    return customizeMode;
  }

  public isBulking(refId: string): boolean {
    let bulking: boolean = false;

    if (this.master.has(refId)) {
      let data: OEDataModel = this.master.get(refId);
      if (data != null && data.bulking != null) {
        bulking = data.bulking === true && data.bulked !== true;
      }
    }

    return bulking;
  }

  public getOperationMode(refId: string): OENOperationMode {
    return this.master.has(refId) ? this.master.get(refId).operationMode : null;
  }

  /**********************************************************************/
  /* Entry Type */

  public isEntryTypeSpot(refId: string): boolean {
    return this.getEntryType(refId) === OENEntryType.SPOT;
  }

  public isEntryTypeBenchmark(refId: string): boolean {
    return this.getEntryType(refId) === OENEntryType.BENCHMARK;
  }

  public isEntryTypeAlgo(refId: string): boolean {
    return this.getEntryType(refId) === OENEntryType.ALGO;
  }

  public isEntryTypeNdfAlgo(refId: string): boolean {
    return this.getEntryType(refId) === OENEntryType.NDF_ALGO;
  }

  public isEntryTypeNdf(refId: string): boolean {
    return this.getEntryType(refId) === OENEntryType.NDF;
  }

  public getEntryType(refId: string): OENEntryType {
    return this.master.has(refId) ? this.master.get(refId).entryType : null;
  }

  /**********************************************************************/
  /* Modification Lock */
  /**
   * Returns a boolean value of isLockedForModification flag, which indicates whether the model has been
   * locked for modification.
   * @param refId <string> - reference id in the model data map
   */
  public isLockedForModification(refId: string): boolean {
    if (this.master.has(refId)) {
      return this.master.get(refId).isLockedForModification;
    }
    return null;
  }

  /**
   * Returns a string value or user Id that thas this order locked or null if order is unlocked
   * @param refId <string> - reference id in the model data map
   */
  public lockUserId(refId: string): string {
    const dataModel = this.master.get(refId);
    if (dataModel) {
      return dataModel.lockUserId;
    }
    return null;
  }

  /**********************************************************************/
  /* Modification Privatized */

  public isModificationPrivatized(refId: string): boolean {
    if (this.master.has(refId)) {
      return this.master.get(refId).isModificationPrivatized;
    }
    return null;
  }

  /**********************************************************************/
  /* Validation errors */

  public hasValidationErrors(refId: string, anyNode: boolean = false): boolean {
    if (this.master.has(refId)) {
      if (anyNode) {
        let propagationTree = this.getStrategyNodesListRoot(refId);
        let errorsFound = false;
        propagationTree.forEach((itemRefId: string) => {
          errorsFound = errorsFound || this.master.get(refId).hasValidationErrors;
        });
        return errorsFound;
      } else {
        return this.master.get(refId).hasValidationErrors;
      }
    }
    return false;
  }

  /**********************************************************************/
  /*  Locking Profile */
  public getLockingProfile(refId: string, strategyOnly = false): OELockingProfileModel {
    if (this.master.hasStrategy(refId)) {
      //CHANGE: if(this.master.has(refId) && this.get(refId).strategy != null){
      if (strategyOnly) {
        return this.master.get(refId).strategy.lockProfile;
      }
      return OEPrivatizationLockingProfileModel.getLockIntersection(
        this.master.get(refId).strategy.lockProfile,
        this.master.get(refId).privatizationLockProfile,
      );
    }
    return null;
  }

  /**********************************************************************/
  /* Tolerance Accept  */

  public getToleranceAccept(refId: string): boolean {
    if (this.master.has(refId)) {
      let rootRefId = this.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        return this.master.get(rootRefId).toleranceAccept;
      }
    }
    return null;
  }

  /**********************************************************************/
  /* Tolerance Accept  */

  public getToleranceAmountAccept(refId: string): boolean {
    if (this.master.has(refId)) {
      let rootRefId = this.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        return this.master.get(rootRefId).toleranceAmountAccept;
      }
    }
    return null;
  }

  /**********************************************************************/
  /* Negative Markup Accept  */

  public getNegativeMarkupAccept(refId: string): boolean {
    if (this.master.has(refId)) {
      let rootRefId = this.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        return this.master.get(rootRefId).negativeMarkupAccept;
      }
    }
    return null;
  }

  /**********************************************************************/
  /* BM Zero Amount  Warning acceptance */

  public getBMZeroAmountAccept(refId: string): boolean {
    if (this.master.has(refId)) {
      let rootRefId = this.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        return this.master.get(rootRefId).bmZeroAmountAccept;
      }
    }
    return null;
  }

  /**********************************************************************/
  /* Clone */

  public isClone(refId: string): boolean {
    if (this.master.has(refId)) {
      return this.master.get(refId).isClone;
    }
    return false;
  }

  /**********************************************************************/
  /*  DATA -Getters */

  public getClientComment(refId: string): string {
    try {
      //this.updateClientCommentValue(refId);
      let comment = this.master.get(refId).clientComment.value;
      return (comment = comment != null ? comment : "");
    } catch (ex) {
      logger.out.error(`EXCEPTION: _oeModel.getClientComment():  %O`, ex);
      return "";
    }
  }

  public getTraderGroup(refId: string): CubiqTS.TradingGroupImpl | string {
    try {
      if (this.master.has(refId)) {
        let traderGroup = this.master.get(refId).traderGroup;
        let traderGroupCodeId = this.master.get(refId).traderGroupCodeId;
        return traderGroup != null ? traderGroup : traderGroupCodeId ? traderGroupCodeId : null;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  public isOrderTypeMKT(refId: string): boolean {
    return (
      this.master.has(refId) &&
      this.master.get(refId).orderType instanceof ICAutocompleteGenericModel &&
      this.master.get(refId).orderType.value == CubiqTS.RequestOrderType.MARKET
    );
  }

  public isGammaStrategy(refId: string): boolean {
    if (this.master.has(refId)) {
      let model: OEDataModel = this.master.get(refId);
      return model.strategy != null && model.strategy.strategyType == CubiqTS.StrategyType.GAMMA;
    }
    return false;
  }

  public isCcyPairFwdEnabled(refId: string): boolean {
    let oeData: OEDataModel = this.get(refId);
    return (
      oeData instanceof OEDataModel &&
      oeData.ccyPairModel instanceof CubiqTS.CurrencyPairImpl &&
      oeData.ccyPairModel.fwdEnabledFg
    );
  }

  /**********************************************************************/
  /*********************** STRATEGIES ***********************************/
  /**********************************************************************/

  // CHANGE: newStrategy();
  protected getNewStrategy(): any {
    return { strategy: new OEStrategyModel() };
  }

  /**********************************************************************/
  /* STRATEGIES - RefId and Order Id */

  // primary sibling is the one that has relationship to the parent
  public isThePrimarySibling(refId: string): boolean {
    if (this.master.hasStrategy(refId) && this.hasSiblings(refId)) {
      let parentRefId = this.master.get(refId).strategy.parentRefId;
      let siblingRefId = this.master.get(refId).strategy.siblings[0].refId;
      return parentRefId !== siblingRefId;
    }
    return false;
  }

  public getRootIdForNodeWithRefId(refId: string): string {
    if (this.isRootNode(refId)) {
      return refId;
    } else if (this.master.hasStrategy(refId)) {
      let legRootId = this.master.get(refId).strategy.rootRefId;
      if (legRootId != null && typeof legRootId === "string") {
        return legRootId;
      } else {
        throw new ReferenceError(`Model with ${refId} has no set root!`);
      }
    } else {
      throw new ReferenceError(`Model with ${refId} not found or no strategy model attached!`);
    }
  }

  public getRootRefIdForStrategyRefId(refId: string): string {
    if (this.isRootNode(refId)) {
      return refId;
    } else if (this.master.hasStrategy(refId)) {
      let legRootId = this.master.get(refId).strategy.rootRefId;
      if (legRootId != null && typeof legRootId === "string") {
        return legRootId;
      }
    }
    return null;
  }

  /**********************************************************************/
  /* STRATEGIES - Root */

  public isRootNode(refId: string): boolean {
    if (this.master.hasStrategy(refId)) {
      return this.master.instance(refId).strategy.isRoot;
    } else {
      if (this.master.has(refId)) {
        return true; // model is valid but has no strategy therefore root
      }
      throw new ReferenceError(`Model with ${refId} not found!`);
    }
  }

  public isRootNodeAlone(refId: string): boolean {
    if (this.isRootNode(refId)) {
      let rootModelStrategy = this.master.instance(refId).strategy;
      return rootModelStrategy.children.length === 0 && rootModelStrategy.siblings.length === 0;
    }
    return false;
  }

  public getRootModel(refId: string): OEDataModel {
    try {
      if (this.master.has(refId) && this.isRootNode(refId)) {
        return this.master.instance(refId);
      } else if (this.master.hasStrategy(refId)) {
        let rootRefId = this.getRootIdForNodeWithRefId(refId);
        if (this.master.has(rootRefId)) {
          return this.master.instance(rootRefId);
        }
      }
      return null;
    } catch (ex) {
      return null;
    }
  }

  /**********************************************************************/
  /* STRATEGIES - Parent */

  public hasParent(refId: string): boolean {
    try {
      if (this.master.hasStrategy(refId) && !this.isRootNode(refId)) {
        let parentRefId = this.master.instance(refId).strategy.parentRefId;
        return parentRefId != null && this.master.has(parentRefId);
      }
    } catch (ex) {}
    return false;
  }

  public getParentRef(refId: string): string {
    try {
      if (this.master.hasStrategy(refId) && !this.isRootNode(refId)) {
        let parentRefId = this.master.instance(refId).strategy.parentRefId;
        return parentRefId != null && this.master.hasStrategy(parentRefId) ? parentRefId : null;
      }
    } catch (ex) {}
    return null;
  }

  public getParentModel(refId: string): OEDataModel | null {
    let parentRefId = this.getParentRef(refId);
    return parentRefId != null ? this.master.get(parentRefId) : null;
  }

  /**********************************************************************/
  /* STRATEGIES - Sibling */
  public hasSiblings(refId: string): boolean {
    if (this.master.hasStrategy(refId)) {
      let model = this.master.get(refId);
      return Array.isArray(model.strategy.siblings) && model.strategy.siblings.length > 0;
    } else {
      return false;
    }
  }

  public hasSiblingLoop(refId: string): boolean {
    if (this.master.hasStrategy(refId)) {
      let model = this.master.get(refId);
      return (
        Array.isArray(model.strategy.siblings) &&
        model.strategy.siblings.length > 0 &&
        model.strategy.siblings[0].type === CubiqTS.OrderRelationshipType.LOOP
      );
    } else {
      return false;
    }
  }

  public getSiblingRefId(refId: string): string {
    if (this.master.hasStrategy(refId) && this.hasSiblings(refId)) {
      let model = this.master.get(refId);
      return model.strategy.siblings[0].refId;
    }
    return null;
  }

  public getSiblingLoopRefId(refId: string): string {
    if (this.master.hasStrategy(refId) && this.hasSiblingLoop(refId)) {
      let model = this.master.get(refId);
      return model.strategy.siblings[0].refId;
    }
    return null;
  }
  /**********************************************************************/
  /* STRATEGIES - Children */

  public hasChildren(refId: string): boolean {
    if (this.master.hasStrategy(refId)) {
      let model = this.master.get(refId);
      return Array.isArray(model.strategy.children) && model.strategy.children.length > 0;
    } else {
      return false;
    }
  }

  /**********************************************************************/
  /* STRATEGIES - Relationships */

  public getRelationshipToParent(refId: string): CubiqTS.OrderRelationshipType | null {
    let parentModel = this.getParentModel(refId);
    if (parentModel != null) {
      // check children
      let theChosenOneChild = parentModel.strategy.children.find((item) => {
        return item.refId === refId;
      });
      if (theChosenOneChild != null) {
        return theChosenOneChild.type;
      }
      // check siblings
      let theChosenOneSibling = parentModel.strategy.siblings.find((item) => {
        return item.refId === refId;
      });
      if (theChosenOneSibling != null) {
        return theChosenOneSibling.type;
      }
    }
    return null;
  }

  /**
   * IMPORTANT: relationship to parent is not considered (use getRelationshipToParent() for that), checks only children and sibling nodes.
   * @param refId
   * @param relType
   */
  public hasRelationshipOfType(refId: string, relType: CubiqTS.OrderRelationshipType): boolean {
    if (this.master.hasStrategy(refId)) {
      if (
        relType === CubiqTS.OrderRelationshipType.IFDONE ||
        relType === CubiqTS.OrderRelationshipType.ON_TIMEOUT
      ) {
        let children = this.get(refId).strategy.children;
        if (
          children != null &&
          children.length > 0 &&
          children.find((item) => {
            return item.type === relType;
          }) != null
        ) {
          return true;
        }
      } else if (
        relType === CubiqTS.OrderRelationshipType.OCO ||
        relType === CubiqTS.OrderRelationshipType.LOOP
      ) {
        let siblings = this.get(refId).strategy.siblings;
        if (
          siblings != null &&
          siblings.length > 0 &&
          siblings.find((item) => {
            return item.type === relType;
          }) != null
        ) {
          return true;
        }
      } else {
        logger.out.error(
          `ERROR: _oeModel.hasRelationshipOfType(): Unknown relationship type= ${relType}`,
        );
      }
    }
    return false;
  }

  public isRelationshipAllowedTimeout(refId: string): boolean {
    if (this.master.has(refId)) {
      let model = this.master.get(refId);
      return model.strategy.permissions.allowTimeout;
    } else {
      return false;
    }
  }

  public isRelationshipAllowedLoop(refId: string): boolean {
    if (this.master.has(refId)) {
      let model = this.master.get(refId);
      return (
        Array.isArray(model.strategy.siblings) &&
        model.strategy.siblings.length === 0 &&
        model.strategy.permissions.allowLoop
      );
    } else {
      return false;
    }
  }

  public isRelationshipAllowedOco(refId: string): boolean {
    if (this.master.has(refId)) {
      let model = this.master.get(refId);
      return (
        Array.isArray(model.strategy.siblings) &&
        model.strategy.siblings.length === 0 &&
        model.strategy.permissions.allowOco
      );
    } else {
      return false;
    }
  }

  public isRelationshipAllowedIfDone(refId: string): boolean {
    if (this.master.has(refId)) {
      let model = this.master.get(refId);
      return Array.isArray(model.strategy.children) && model.strategy.permissions.allowIfDone;
    } else {
      return false;
    }
  }

  /**********************************************************************/
  /* STRATEGY GRAPH - GETTERS  */

  /**
   * Returns refIds of all nodes in the strategy graph including the root, no mather which refId is passed as argument
   * @param refId
   * @returns
   */
  public getStrategyNodesListRoot(refId: string): Array<string> {
    let rootNodesList: Array<string> = new Array<string>();
    try {
      let rootRefId: string = refId;
      if (this.master.hasStrategy(refId)) {
        if (!this.isRootNode(refId)) {
          rootRefId = this.master.get(refId).strategy.rootRefId;
        }
        this.exploreStrategyGraphFull(rootRefId, rootNodesList);
      } else if (this.master.has(refId) && this.isRootNodeAlone(refId)) {
        // only root
        rootNodesList.push(refId);
      }
    } catch (ex) {
      logger.out.error(`EXCEPTION: _oeModel.getStrategyNodesListRoot(): %O`, ex);
    }
    return rootNodesList;
  }

  public getStrategyNodesListPartial(refId: string): Array<string> {
    let partialNodesList: Array<string> = new Array<string>();
    let parentRefId: string = this.master.get(refId).strategy.parentRefId;

    if (this.master.hasStrategy(refId))
      this.exploreStrategyGraphPartial(refId, partialNodesList, parentRefId);

    //logger.out.info("INFO Strategy propagation graph PARTIAL: ", partialNodesList);
    return partialNodesList;
  }

  /**
   * This function facilitates FULL BFS traversal of the strategy graph
   * @param refId <string>: preferably this should be the root graph
   * @param nodes Array<string> after execution contains all nodes in the strategy graph including the root node
   */
  private exploreStrategyGraphFull(refId: string, nodes: Array<string>): void {
    if (nodes.indexOf(refId) < 0) nodes.push(refId);
    if (this.master.hasStrategy(refId)) {
      let newNodes = this.master
        .get(refId)
        .strategy.siblings.map((relative) => {
          return relative.refId;
        })
        .concat(
          this.master.get(refId).strategy.children.map((child) => {
            return child.refId;
          }),
        )
        .filter((item) => {
          return nodes.indexOf(item) < 0;
        });
      newNodes.forEach((newItem) => {
        this.exploreStrategyGraphFull(newItem, nodes);
      });
    }
  }

  /**
   * This function facilitates Partial BFS traversal of the strategy graph
   * @param refId <string>: starting point of the exploration
   * @param nodes Array<string> after execution contains all nodes which children or siblings (does not include the root)
   */
  private exploreStrategyGraphPartial(
    refId: string,
    nodes: Array<string>,
    parentRefId: string,
  ): void {
    if (nodes.indexOf(refId) < 0 && !this.isRootNode(refId) && refId !== parentRefId)
      nodes.push(refId);
    if (this.master.hasStrategy(refId)) {
      let newNodes = this.master
        .get(refId)
        .strategy.siblings.map((relative) => {
          return relative.refId;
        })
        .concat(
          this.master.get(refId).strategy.children.map((child) => {
            return child.refId;
          }),
        )
        .filter((item) => {
          return nodes.indexOf(item) < 0 && item !== parentRefId;
        });
      newNodes.forEach((newItem) => {
        this.exploreStrategyGraphPartial(newItem, nodes, parentRefId);
      });
    }
  }

  /**********************************************************************/
  /* STRATEGY GRAPH - Complex */

  public doesStrategyHaveMKTLeg(refId: string): boolean {
    if (this.has(refId)) {
      return this.doesStrategyHaveOrderOfType(refId, CubiqTS.RequestOrderType.MARKET);
    }
    return false;
  }

  public doesStrategyHaveOrderOfType(refId: string, value: CubiqTS.RequestOrderType): boolean {
    let nodeList: Array<string> = this.getStrategyNodesListRoot(refId);
    if (Array.isArray(nodeList) && nodeList.length > 0) {
      for (let nodeItem of nodeList) {
        let orderData = this.get(nodeItem);
        if (
          orderData instanceof OEDataModel &&
          orderData.orderType instanceof ICAutocompleteGenericModel &&
          orderData.orderType.value === value
        ) {
          return true;
        }
      }
    }
    return false;
  }

  /**********************************************************************/
  /* HELPER */

  public generateRandomRefId(): string {
    return uuid();
  }

  /* ******************************************** */
  /* LOGOUT */
  ///// ADDED AS PART OF OMS-28059
  //USED TO DETERMINE IF ENTRY MODEL HAVE ORDERS IN DI STATE

  public hasOrdersInDI(): boolean {
    for (let item of this.master.getAll().values()) {
      if (this.isSrvOrderState(item.refId, CubiqTS.OrderState.DI_NEEDED)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns true if the order ticket is monitoring the execution of an order after updates
   * @param refId
   * @returns
   */
  public monitoringExecution(refId: string): boolean {
    const dataModel = this.master.get(refId);
    if (dataModel) {
      switch (dataModel.entryType) {
        case OENEntryType.ALGO:
        case OENEntryType.NDF_ALGO:
          return (
            TERMINAL_DISPLAY_STATES.indexOf(dataModel.displayState) !== -1 &&
            dataModel.displayState !== OENDisplayState.SHOW_REJECTED
          );
        case OENEntryType.SPOT:
        case OENEntryType.NDF:
        case OENEntryType.BENCHMARK:
        case OENEntryType.GAMMA:
          return false;
      }
    }
    return false;
  }

  /**
   * Returns true if the order ticket supports continued execution monitoring after completion
   * @param refId
   * @returns
   */
  public supportsMonitoringExecution(refId: string): boolean {
    const dataModel = this.master.get(refId);
    if (dataModel) {
      switch (dataModel.entryType) {
        case OENEntryType.ALGO:
        case OENEntryType.NDF_ALGO:
          return true;
        case OENEntryType.NDF:
        case OENEntryType.SPOT:
        case OENEntryType.BENCHMARK:
        case OENEntryType.GAMMA:
          return false;
      }
    }
    return false;
  }

  /**
   * Is the specified order locked by a different user than the current active user?
   * @param refId
   * @returns
   */
  public lockedByAnotherUser(refId: string): boolean {
    const dataModel = this.master.get(refId);
    if (dataModel) {
      const lockUser = dataModel.lockUserId;
      if (lockUser && lockUser !== this.settings.currentUserId) {
        return true;
      }
    }
    return false;
  }
}
